import {
  Button,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Text,
} from "@/components/ui";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useMatterInsuranceVehiclePermissions } from "@/hooks/useMatterInsuranceVehiclePermissions";
import type { Matter, MatterInsurance } from "@/lib/types";
import { getInsuranceContactConnections } from "@/lib/utils/connections/insurance-contact-connections";
import {
  formatContactsName,
  formatInsuranceTitle,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Info, Mail, Pencil, Phone } from "lucide-react";

const INSURANCE_ADJUSTER_ROLES = [
  "Bodily Injury Adjuster",
  "Property Damage Adjuster",
  "MedPay Adjuster",
  "Adjuster Supervisor",
] as const;

export function InsurancePolicyHoverCard({
  matter,
  insurance,
  partyLabel,
  partyTab,
  limitLabel,
}: {
  matter: Matter;
  insurance: MatterInsurance;
  partyLabel: "1st Party" | "3rd Party";
  partyTab: "client" | "defendant";
  limitLabel: string;
}) {
  const { openInsuranceModal } = useGlobalModal();
  const { hasUpdateInsurancePermission } =
    useMatterInsuranceVehiclePermissions();
  const insuranceTitle = formatInsuranceTitle({ insurance });
  const adjusters = INSURANCE_ADJUSTER_ROLES.flatMap((label) =>
    getInsuranceContactConnections(insurance, label).map((connection) => ({
      connection,
      label,
    })),
  );
  const insurerName = insuranceTitle?.insurerPart ?? "Unknown insurer";
  const policyCategory = partyLabel === "3rd Party" ? "Liability" : "Coverage";

  return (
    <HoverCard openDelay={150} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors"
          aria-label={`View ${insurerName} policy details`}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        side="bottom"
        className="w-[min(19rem,calc(100vw-2rem))] overflow-hidden rounded-xl p-0 shadow-lg"
      >
        <div className="border-b px-3.5 py-3">
          <Text
            as="p"
            variant="body"
            className="text-[15px] leading-5 font-semibold"
          >
            {insurerName}
          </Text>
          <Text
            as="p"
            variant="subHeading"
            className="mt-0.5 text-xs leading-4"
          >
            {`${policyCategory} · ${partyLabel} · ${limitLabel}`}
          </Text>
        </div>

        <div className="space-y-3 p-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0">
              <Text
                as="p"
                variant="caption"
                color="muted"
                className="text-[10px] font-semibold tracking-[0.14em] uppercase"
              >
                Policy #
              </Text>
              <Text as="p" variant="body" className="mt-0.5 truncate text-sm">
                {insurance.policyNumber || "—"}
              </Text>
            </div>
            <div className="min-w-0">
              <Text
                as="p"
                variant="caption"
                color="muted"
                className="text-[10px] font-semibold tracking-[0.14em] uppercase"
              >
                Claim #
              </Text>
              <Text as="p" variant="body" className="mt-0.5 truncate text-sm">
                {insurance.claimNumber || "—"}
              </Text>
            </div>
          </div>

          <div className="border-t pt-3">
            <Text
              as="p"
              variant="caption"
              color="muted"
              className="text-[10px] font-semibold tracking-[0.14em] uppercase"
            >
              Adjuster
            </Text>
            {adjusters.length > 0 ? (
              <div className="mt-1 space-y-2.5">
                {adjusters.map(({ connection: adjuster, label }) => {
                  const adjusterEmail = adjuster.contact?.primaryEmail?.address;
                  const adjusterPhone = adjuster.contact?.primaryPhone;

                  return (
                    <div
                      key={adjuster.id}
                      className="not-first:border-t not-first:pt-2.5"
                    >
                      <Text as="p" variant="body" className="text-sm">
                        {formatContactsName(adjuster.contact ?? undefined) ||
                          "Unnamed adjuster"}
                      </Text>
                      <Text
                        as="p"
                        variant="caption"
                        color="muted"
                        className="mt-0.5 text-[11px]"
                      >
                        {label}
                      </Text>
                      {adjusterEmail ? (
                        <Text
                          as="span"
                          variant="subHeading"
                          color="primary"
                          className="mt-1.5 flex items-center gap-1.5 text-[13px]"
                        >
                          <Mail className="h-3.5 w-3.5 shrink-0" />
                          {adjusterEmail}
                        </Text>
                      ) : null}
                      {adjusterPhone?.number ? (
                        <Text
                          as="span"
                          variant="subHeading"
                          color="primary"
                          className="mt-1 flex items-center gap-1.5 text-[13px]"
                        >
                          <Phone className="h-3.5 w-3.5 shrink-0" />
                          {formatLocalOrIntlPhoneNumber(adjusterPhone)}
                        </Text>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Text as="p" variant="body" className="mt-0.5 text-sm">
                No adjuster assigned
              </Text>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-2.5">
            {hasUpdateInsurancePermission ? (
              <Button
                type="button"
                variant="link"
                className="h-auto p-0 text-[13px] font-medium text-blue-500"
                onClick={() => {
                  openInsuranceModal({
                    matterId: matter.id,
                    insuranceId: insurance.id,
                    editing: true,
                    initialTab: "details",
                  });
                }}
              >
                <Pencil className="h-3.5 w-3.5 shrink-0" />
                Edit insurance
              </Button>
            ) : null}
            <Link
              to="/matters/$matterId/matter-details/$tab"
              params={{ matterId: matter.id, tab: partyTab }}
              search={{ insuranceId: insurance.id }}
              className="inline-flex max-w-full text-blue-500"
            >
              <Text
                as="span"
                variant="subHeading"
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-blue-500 hover:underline"
              >
                <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
                View in parties
              </Text>
            </Link>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
